import { useContext, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import StaffPageShell from '../components/StaffPageShell';
import MessageDisplay from '../components/MessageDisplay';
import SearchableSelect from '../components/Form/SearchableSelect';
import useNotificationsAdmin from '../hooks/useNotificationsAdmin';
import '../styles/notifications.css';

const TABS = [
  { id: 'compose', label: 'Compose' },
  { id: 'history', label: 'History' },
  { id: 'settings', label: 'Settings' },
];

export default function AdminNotificationsPage() {
  const { user } = useContext(AuthContext);
  const authUser = user?.user || user;
  const [tab, setTab] = useState('compose');

  const {
    canSend,
    canManage,
    settings,
    compose,
    updateCompose,
    preview,
    runPreview,
    send,
    sending,
    campaigns,
    selectedCampaign,
    setSelectedCampaign,
    openCampaign,
    batches,
    loading,
    savingSettings,
    saveSettings,
    error,
    success,
  } = useNotificationsAdmin(authUser);

  if (!canSend) {
    return <Navigate to="/dashboard" replace />;
  }

  const toggleChannel = (channel) => {
    const has = compose.channels.includes(channel);
    updateCompose({
      channels: has
        ? compose.channels.filter((c) => c !== channel)
        : [...compose.channels, channel],
    });
  };

  const toggleBatch = (batchId) => {
    const id = String(batchId);
    const has = compose.batchIds.includes(id);
    updateCompose({
      batchIds: has
        ? compose.batchIds.filter((b) => b !== id)
        : [...compose.batchIds, id],
    });
  };

  return (
    <StaffPageShell
      title="Notifications"
      subtitle="Compose email and in-app messages; SMS/WhatsApp ready when enabled"
      wide
    >
      <MessageDisplay error={error} success={success} />

      <div className="notif-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            className={`notif-tab ${tab === t.id ? 'is-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="notif-muted">Loading…</p>
      ) : null}

      {!loading && tab === 'compose' ? (
        <div className="notif-panel notif-grid">
          <div className="notif-field">
            <label htmlFor="notif-subject">Subject</label>
            <input
              id="notif-subject"
              value={compose.subject}
              onChange={(e) => updateCompose({ subject: e.target.value })}
              placeholder="Message subject"
            />
          </div>
          <div className="notif-field">
            <label htmlFor="notif-body">Message</label>
            <textarea
              id="notif-body"
              value={compose.body}
              onChange={(e) => updateCompose({ body: e.target.value })}
              placeholder="Write your message…"
            />
          </div>

          <div className="notif-field">
            <label>Channels</label>
            <div className="notif-check-row">
              {[
                { id: 'email', label: 'Email', enabled: settings?.emailEnabled !== false },
                { id: 'in_app', label: 'In-app', enabled: settings?.inAppEnabled !== false },
                { id: 'sms', label: 'SMS (stub)', enabled: settings?.smsEnabled },
                { id: 'whatsapp', label: 'WhatsApp (stub)', enabled: settings?.whatsappEnabled },
              ].map((ch) => (
                <label key={ch.id}>
                  <input
                    type="checkbox"
                    checked={compose.channels.includes(ch.id)}
                    disabled={!ch.enabled}
                    onChange={() => toggleChannel(ch.id)}
                  />
                  {ch.label}
                </label>
              ))}
            </div>
          </div>

          <div className="notif-field">
            <label htmlFor="notif-audience">Audience</label>
            <SearchableSelect
              id="notif-audience"
              value={compose.audienceType}
              onChange={(e) => updateCompose({ audienceType: e.target.value })}
              required
              options={[
                { value: 'batch', label: 'Batch(es)' },
                { value: 'role', label: 'All by role' },
                { value: 'students', label: 'Student IDs (multi)' },
                { value: 'single', label: 'Single student ID' },
              ]}
            />
          </div>

          {compose.audienceType === 'role' ? (
            <div className="notif-field">
              <label htmlFor="notif-role">Role</label>
              <SearchableSelect
                id="notif-role"
                value={compose.role}
                onChange={(e) => updateCompose({ role: e.target.value })}
                required
                options={[
                  { value: 'student', label: 'Students' },
                  { value: 'trainer', label: 'Trainers' },
                  { value: 'recruiter', label: 'Recruiters' },
                  { value: 'staff', label: 'Staff' },
                  { value: 'admin', label: 'Admins' },
                ]}
              />
            </div>
          ) : null}

          {compose.audienceType === 'batch' ? (
            <div className="notif-field">
              <label>Batches</label>
              <div className="notif-check-row" style={{ maxHeight: 180, overflow: 'auto' }}>
                {batches.length === 0 ? (
                  <p className="notif-muted">No batches available</p>
                ) : (
                  batches.map((b) => (
                    <label key={b.id}>
                      <input
                        type="checkbox"
                        checked={compose.batchIds.includes(String(b.id))}
                        onChange={() => toggleBatch(b.id)}
                      />
                      {b.batchName}
                    </label>
                  ))
                )}
              </div>
            </div>
          ) : null}

          {compose.audienceType === 'students' || compose.audienceType === 'single' ? (
            <div className="notif-field">
              <label htmlFor="notif-student-ids">
                {compose.audienceType === 'single' ? 'Student ID' : 'Student IDs (comma or space separated)'}
              </label>
              <textarea
                id="notif-student-ids"
                value={compose.studentIdsText}
                onChange={(e) => updateCompose({ studentIdsText: e.target.value })}
                style={{ minHeight: 80 }}
              />
            </div>
          ) : null}

          {preview ? (
            <p className="notif-muted">
              Preview: {preview.recipientCount} people · ~{preview.deliveryEstimate} deliveries ·
              channels: {(preview.channels || []).join(', ')}
            </p>
          ) : null}

          <div className="notif-actions">
            <button type="button" className="notif-btn" onClick={runPreview}>
              Preview audience
            </button>
            <button
              type="button"
              className="notif-btn notif-btn-primary"
              disabled={sending}
              onClick={send}
            >
              {sending ? 'Sending…' : 'Send notification'}
            </button>
          </div>
        </div>
      ) : null}

      {!loading && tab === 'history' ? (
        <div className="notif-panel">
          {selectedCampaign ? (
            <>
              <div className="notif-actions" style={{ marginTop: 0 }}>
                <button
                  type="button"
                  className="notif-btn"
                  onClick={() => setSelectedCampaign(null)}
                >
                  Back to list
                </button>
              </div>
              <h2 style={{ margin: '0.75rem 0 0.35rem', fontSize: '1.1rem' }}>
                {selectedCampaign.subject}
              </h2>
              <p className="notif-muted">
                Status: {selectedCampaign.status} · Channels:{' '}
                {(selectedCampaign.channels || []).join(', ')} · Recipients:{' '}
                {selectedCampaign.recipients?.length || 0}
              </p>
              <p style={{ whiteSpace: 'pre-wrap', margin: '0.75rem 0' }}>{selectedCampaign.body}</p>
              <div style={{ overflowX: 'auto' }}>
                <table className="notif-table">
                  <thead>
                    <tr>
                      <th>Recipient</th>
                      <th>Channel</th>
                      <th>Status</th>
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedCampaign.recipients || []).map((r) => (
                      <tr key={r.id}>
                        <td>
                          {r.displayName || r.targetId}
                          {r.email ? <div className="notif-muted">{r.email}</div> : null}
                        </td>
                        <td>{r.channel}</td>
                        <td>
                          <span className="notif-status">{r.status}</span>
                        </td>
                        <td className="notif-muted">{r.errorMessage || r.sentAt || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="notif-table">
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Recipients</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="notif-muted">
                        No campaigns yet
                      </td>
                    </tr>
                  ) : (
                    campaigns.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <button
                            type="button"
                            className="linkish"
                            onClick={() => openCampaign(c.id)}
                          >
                            {c.subject}
                          </button>
                        </td>
                        <td>
                          <span className="notif-status">{c.status}</span>
                        </td>
                        <td>{c._count?.recipients ?? '—'}</td>
                        <td className="notif-muted">
                          {c.createdAt ? new Date(c.createdAt).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {!loading && tab === 'settings' ? (
        <div className="notif-panel notif-grid">
          {!canManage ? (
            <p className="notif-muted">
              You can view settings. Only admins with manage permission can change them.
            </p>
          ) : null}
          {[
            {
              key: 'allowTrainerSend',
              label: 'Allow trainers to send (scoped to their batches)',
            },
            {
              key: 'allowParentRecipients',
              label: 'Include parents as recipients when contact info exists',
            },
            { key: 'emailEnabled', label: 'Email channel enabled' },
            { key: 'inAppEnabled', label: 'In-app channel enabled' },
            { key: 'smsEnabled', label: 'SMS channel enabled (stub until provider configured)' },
            {
              key: 'whatsappEnabled',
              label: 'WhatsApp channel enabled (stub until provider configured)',
            },
          ].map((row) => (
            <label key={row.key} className="notif-check-row" style={{ gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={Boolean(settings?.[row.key])}
                disabled={!canManage || savingSettings}
                onChange={(e) => saveSettings({ [row.key]: e.target.checked })}
              />
              <span>{row.label}</span>
            </label>
          ))}

          <p className="notif-muted" style={{ marginTop: '1rem' }}>
            Event-driven notification toggles (welcome, module complete, placed, etc.) are managed
            under{' '}
            <Link to="/admin/feature-flags" style={{ color: 'var(--color-primary)' }}>
              Feature flags
            </Link>
            .
          </p>
        </div>
      ) : null}
    </StaffPageShell>
  );
}
